import { useCallback, useEffect, useMemo, useRef } from 'react'
import { trackClientEvent } from 'analytics/trackClientEvent'

export type CustomInteractionTimingKey = 'search_drawer'

export interface CustomInteractionTimingToken {
  readonly key: CustomInteractionTimingKey
  readonly id: symbol
}

interface PendingTiming {
  token: CustomInteractionTimingToken
  startedAt: number
}

interface CustomInteractionTiming {
  start: (key: CustomInteractionTimingKey, timestamp: number) => CustomInteractionTimingToken
  stop: (token: CustomInteractionTimingToken, timestamp: number) => void
  abort: (token: CustomInteractionTimingToken) => void
}

/** Own in the common parent; async work must capture the token returned by start. */
export default function useCustomInteractionTiming(): CustomInteractionTiming {
  const timings = useRef(new Map<CustomInteractionTimingKey, PendingTiming>())
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    const pending = timings.current
    return () => {
      mounted.current = false
      pending.clear()
    }
  }, [])

  const start = useCallback((key: CustomInteractionTimingKey, timestamp: number): CustomInteractionTimingToken => {
    const token = { key, id: Symbol(key) }
    timings.current.delete(key)
    if (mounted.current && Number.isFinite(timestamp) && timestamp >= 0) {
      timings.current.set(key, { token, startedAt: timestamp })
    }
    return token
  }, [])

  const stop = useCallback((token: CustomInteractionTimingToken, timestamp: number): void => {
    const pending = timings.current.get(token.key)
    if (pending?.token !== token) return
    timings.current.delete(token.key)
    if (!Number.isFinite(timestamp) || timestamp < pending.startedAt) return
    trackClientEvent({
      subject: `${token.key}_cit_ms`,
      action: String(Math.round(timestamp - pending.startedAt)),
      category: 'performance',
      type: 'nonInteraction',
    })
  }, [])

  const abort = useCallback((token: CustomInteractionTimingToken): void => {
    if (timings.current.get(token.key)?.token === token) timings.current.delete(token.key)
  }, [])

  return useMemo(() => ({ start, stop, abort }), [start, stop, abort])
}
